import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { MemberWithRatings, GroupSummary, RestoreVersionMode } from "../types";
import { isGroupLocked } from "../types";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { useGroupStore } from "../store/groupStore";
import { useSessionStore } from "../store/sessionStore";
import { confirmAction, notify, useUiStore } from "../store/uiStore";
import { PageHeader } from "../components/PageHeader";
import { ErrorState, SkeletonStatStrip } from "../components/ScreenStates";
import { GroupOverviewHeader } from "../components/group/GroupOverviewHeader";
import { GroupSummaryBand } from "../components/group/GroupSummaryBand";
import { MembersGrid } from "../components/group/MembersGrid";
import { MembersFilterBar } from "../components/group/MembersFilterBar";
import { applyMemberFilter, type MemberFilter } from "../components/group/memberFilter";
import { SendToSalesModal } from "../components/group/SendToSalesModal";
import { RestoreVersionModal } from "../components/group/RestoreVersionModal";
import { LOCKED_TOOLTIP, SUPERSEDED_TOOLTIP } from "../components/group/memberOrdering";
import { memberDisplayName } from "../utils/format";
import { gradientDoeDraft } from "../utils/gradientDoe";

type OpenModal = "none" | "sendToSales" | "restore";

export function GroupOverviewPage() {
  const { groupId = "" } = useParams();
  const navigate = useNavigate();
  const loadState = useGroupStore((state) => state.loadState);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const totals = useGroupStore((state) => (state.group?.id === groupId ? state.totals : null));
  const topConditions = useGroupStore((state) => state.topConditions);
  const loadGroup = useGroupStore((state) => state.loadGroup);
  const updateMemberFlags = useGroupStore((state) => state.updateMemberFlags);
  const removeMember = useGroupStore((state) => state.removeMember);
  const toggleMemberExcluded = useGroupStore((state) => state.toggleMemberExcluded);
  const isRecalculating = useGroupStore((state) => state.isRecalculating);
  const saveMember = useGroupStore((state) => state.saveMember);
  const updateGroup = useGroupStore((state) => state.updateGroup);
  const setOverrideMultiplierPreview = useGroupStore((state) => state.setOverrideMultiplierPreview);
  const sendToSales = useGroupStore((state) => state.sendToSales);
  const reopenGroup = useGroupStore((state) => state.reopenGroup);
  const restoreVersion = useGroupStore((state) => state.restoreVersion);
  const otherVersions = useGroupStore((state) => state.otherVersions);
  const latestVersion = otherVersions.reduce<GroupSummary | null>((best, entry) => (best === null || entry.version > best.version ? entry : best), null);
  const currentUserId = useSessionStore((state) => state.user?.id ?? null);
  const highlightedMemberId = useUiStore((state) => state.highlightedMemberId);
  const setHighlightedMemberId = useUiStore((state) => state.setHighlightedMemberId);
  const [openModal, setOpenModal] = useState<OpenModal>("none");
  const [busyAction, setBusyAction] = useState<"none" | "gradient" | "claim" | "reopen">("none");
  const [memberFilter, setMemberFilter] = useState<MemberFilter>("all");
  const isLocked = group ? isGroupLocked(group) : false;
  const lockedTitle = !group || !isLocked ? undefined : group.supersededByGroupId ? SUPERSEDED_TOOLTIP : LOCKED_TOOLTIP;
  const breadcrumb = useMemo(() => (group ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }] : null), [group]);
  usePageSetup("Group Overview", breadcrumb);

  useEffect(() => {
    const controller = new AbortController();
    void loadGroup(groupId, controller.signal);
    return () => controller.abort();
  }, [groupId, loadGroup]);

  useEffect(() => {
    if (!highlightedMemberId) return;
    const timer = setTimeout(() => setHighlightedMemberId(null), 2000);
    return () => clearTimeout(timer);
  }, [highlightedMemberId, setHighlightedMemberId]);

  const handleAddMember = useCallback(() => {
    if (!isLocked) navigate(`/uw/groups/${groupId}/members/new`);
  }, [navigate, groupId, isLocked]);
  const handleEditGroup = useCallback(() => {
    if (!isLocked) navigate(`/uw/groups/${groupId}/edit`);
  }, [navigate, groupId, isLocked]);
  const shortcuts = useMemo(() => ({ add: handleAddMember, edit: handleEditGroup }), [handleAddMember, handleEditGroup]);
  useShortcuts(shortcuts);

  const handleFlagChange = useCallback((memberId: string, patch: Parameters<typeof updateMemberFlags>[1]) => void updateMemberFlags(memberId, patch), [updateMemberFlags]);
  // Feedback 3.2: editing a member starts at the Member Overview; demographics are a step further in.
  const handleEditMember = useCallback((memberId: string) => navigate(`/uw/groups/${groupId}/members/${memberId}`), [navigate, groupId]);

  const handleToggleExcluded = useCallback(
    async (member: MemberWithRatings, excluded: boolean) => {
      const toggled = await toggleMemberExcluded(member.id, excluded);
      if (!toggled) return;
      const name = memberDisplayName(member);
      notify(excluded ? `Excluded ${name}.` : `Included ${name}.`, "success");
      for (const cohortId of toggled.autoExcludedSpockMemberIds) {
        const cohort = toggled.detail.group.members.find((candidate) => candidate.id === cohortId);
        if (cohort) notify(`Spock ${cohort.cohortYear} ${cohort.cohortGender} cohort was excluded because no real member with that birth year and sex remains.`, "info");
      }
    },
    [toggleMemberExcluded],
  );

  const handleRemove = useCallback(
    async (member: MemberWithRatings) => {
      const name = memberDisplayName(member);
      const confirmed = await confirmAction({ title: "Remove member", message: `Remove ${name} and all assigned ratings?`, confirmLabel: "Remove", variant: "danger" });
      if (!confirmed) return;
      if (await removeMember(member.id)) notify(`Removed ${name}.`, "success");
    },
    [removeMember],
  );

  const handleAddGradientDoe = async () => {
    const confirmed = await confirmAction({ title: "Add Gradient Doe", message: "Add the fixed Gradient Doe member (Employee, F, born 10/23/1825, 5'6\", 150 lbs)?", confirmLabel: "Add Gradient Doe", variant: "primary" });
    if (!confirmed) return;
    setBusyAction("gradient");
    const saved = await saveMember(null, gradientDoeDraft());
    setBusyAction("none");
    if (saved) {
      setHighlightedMemberId(saved.id);
      notify("Added Gradient Doe.", "success");
    }
  };

  const handleClaim = async () => {
    if (!currentUserId) return;
    setBusyAction("claim");
    const saved = await updateGroup({ underwriterUserId: currentUserId });
    setBusyAction("none");
    if (saved) notify("You are now the underwriter on this group.", "success");
  };

  const handleSendToSales = async () => {
    const result = await sendToSales();
    setOpenModal("none");
    if (!result) return;
    notify(`${result.detail.group.name} v${result.detail.group.version} was sent to Sales: ${result.census.members.length} members with their debits are now on the Quote.`, "success");
  };

  const handleReopen = async () => {
    if (!group) return;
    const confirmed = await confirmAction({ title: "Reopen as new version", message: `Create version ${group.version + 1} of ${group.name} to work on? Version ${group.version} stays as sent to Sales, and Sales gets the new results when you send again.`, confirmLabel: "Create new version", variant: "primary" });
    if (!confirmed) return;
    setBusyAction("reopen");
    const result = await reopenGroup();
    setBusyAction("none");
    if (result) {
      notify(`Version ${result.detail.group.version} created. Version ${group.version} is read-only.`, "success");
      navigate(`/uw/groups/${result.detail.group.id}`);
    }
  };

  const handleRestore = async (mode: RestoreVersionMode) => {
    if (!group) return;
    const result = await restoreVersion(mode);
    setOpenModal("none");
    if (!result) return;
    const target = result.detail.group;
    notify(mode === "replaceLatest" ? `Version ${target.version} now has version ${group.version}'s members and ratings.` : `Version ${target.version} created from version ${group.version}.`, "success");
    navigate(`/uw/groups/${target.id}`);
  };

  if (loadState.kind === "error" && !group) return <ErrorState message={loadState.message} onRetry={() => void loadGroup(groupId)} />;

  if (!group || !totals) {
    return (
      <div className="space-y-4">
        <PageHeader title="Loading group" />
        <SkeletonStatStrip tiles={4} />
        <MembersGrid groupId={groupId} members={[]} totals={null} isLocked isLoading highlightedMemberId={null} onFlagChange={() => undefined} onEdit={() => undefined} onToggleExcluded={() => undefined} onRemove={() => undefined} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <GroupOverviewHeader
        group={group}
        totals={totals}
        otherVersions={otherVersions}
        isLocked={isLocked}
        lockedTitle={lockedTitle}
        currentUserId={currentUserId}
        isAddingGradient={busyAction === "gradient"}
        isClaiming={busyAction === "claim"}
        isReopening={busyAction === "reopen"}
        onAddMember={handleAddMember}
        onAddGradientDoe={() => void handleAddGradientDoe()}
        onClaim={() => void handleClaim()}
        onEditGroup={handleEditGroup}
        onSendToSales={() => setOpenModal("sendToSales")}
        onReopen={() => void handleReopen()}
        onRestore={() => setOpenModal("restore")}
        onViewSentCensus={() => navigate(`/uw/groups/${groupId}/sent-census`)}
      />
      <GroupSummaryBand groupId={groupId} totals={totals} recalculating={isRecalculating} multiplier={group.uwOverrideMultiplier} isLocked={isLocked} isRenewal={group.isRenewal} topConditions={topConditions} onPreviewMultiplier={setOverrideMultiplierPreview} onCommitMultiplier={(multiplier) => updateGroup({ uwOverrideMultiplier: multiplier })} />
      <MembersFilterBar members={group.members} value={memberFilter} onChange={setMemberFilter} />
      <MembersGrid groupId={groupId} members={applyMemberFilter(group.members, memberFilter)} totals={totals} isLocked={isLocked} lockedTitle={lockedTitle} isLoading={false} filterEmptyMessage={memberFilter === "synthetic" ? "No Spock cohorts or Gradient Doe in this group." : undefined} highlightedMemberId={highlightedMemberId} onFlagChange={handleFlagChange} onEdit={handleEditMember} onToggleExcluded={(member, excluded) => void handleToggleExcluded(member, excluded)} onRemove={(member) => void handleRemove(member)} />
      {openModal === "sendToSales" && <SendToSalesModal group={group} otherVersionCount={otherVersions.length} onConfirm={handleSendToSales} onClose={() => setOpenModal("none")} />}
      {openModal === "restore" && latestVersion && <RestoreVersionModal group={group} latest={latestVersion} onConfirm={handleRestore} onClose={() => setOpenModal("none")} />}
    </div>
  );
}
